/**
 * Categorizes sync errors to determine appropriate handling.
 * Distinguishes between expected offline behavior and critical API errors.
 */

export type SyncErrorCategory = 'network' | 'client' | 'server' | 'auth' | 'workspace-limit' | 'unknown'

/**
 * Custom WebSocket close code used by moly-sync when the per-user
 * concurrent-workspace limit is exceeded. Kept in sync with
 * WORKSPACE_LIMIT_CLOSE_CODE in moly-sync/src/index.ts.
 */
export const WORKSPACE_LIMIT_CLOSE_CODE = 4429

export interface CategorizedSyncError {
  category: SyncErrorCategory
  isCritical: boolean
  statusCode?: number
  message: string
  originalError: Error
}

/**
 * Categorizes a sync error based on HTTP status code and error type.
 *
 * Error Categories:
 * - **network**: Offline, timeout, connection refused (NOT critical - expected for offline-first)
 * - **auth**: 401/403 unauthorized/forbidden (CRITICAL - invalid credentials/permissions)
 * - **client**: 400/404/422 bad request/validation errors (CRITICAL - API contract mismatch)
 * - **server**: 500/502/503 server errors (CRITICAL - backend issue)
 * - **unknown**: Any other error (treat as critical to be safe)
 *
 * @param error - The error to categorize
 * @returns Categorized error with handling instructions
 */
export function categorizeSyncError(error: Error): CategorizedSyncError {
  const message = error.message.toLowerCase()

  // Workspace-limit rejection (WebSocket close code 4429 from moly-sync).
  // Detected via the literal reason string surfaced by sharedWebSocketManager.
  if (message.includes('workspace_limit_exceeded')) {
    return {
      category: 'workspace-limit',
      isCritical: true,
      message:
        'This account already has the maximum number of workspaces open in other tabs. Close one and retry.',
      originalError: error,
    }
  }

  // Extract HTTP status code from error message if present
  const statusMatch = message.match(/\b(\d{3})\b/)
  const statusCode = statusMatch?.[1] ? parseInt(statusMatch[1], 10) : undefined

  // Network/Offline errors (NOT critical - expected behavior)
  if (
    message.includes('failed to fetch') ||
    message.includes('network request failed') ||
    message.includes('networkerror') ||
    message.includes('timeout') ||
    message.includes('econnrefused') ||
    message.includes('connection refused') ||
    message.includes('offline') ||
    error.name === 'NetworkError' ||
    error.name === 'TypeError' && message.includes('fetch')
  ) {
    return {
      category: 'network',
      isCritical: false,
      message: 'Unable to connect to server. Working offline.',
      originalError: error,
    }
  }

  // Auth errors (CRITICAL - invalid credentials/permissions)
  if (statusCode === 401 || statusCode === 403) {
    return {
      category: 'auth',
      isCritical: true,
      statusCode,
      message: statusCode === 401
        ? 'Your session has expired. Please sign in again.'
        : 'You do not have permission to access this workspace.',
      originalError: error,
    }
  }

  // Client errors (CRITICAL - API contract mismatch, validation errors)
  if (statusCode && statusCode >= 400 && statusCode < 500) {
    return {
      category: 'client',
      isCritical: true,
      statusCode,
      message: 'There was a problem syncing your data. Please refresh the page or contact support.',
      originalError: error,
    }
  }

  // Server errors (CRITICAL - backend issue)
  if (statusCode && statusCode >= 500) {
    return {
      category: 'server',
      isCritical: true,
      statusCode,
      message: 'The server is experiencing issues. Please try again later.',
      originalError: error,
    }
  }

  // Unknown errors (treat as critical to be safe)
  return {
    category: 'unknown',
    isCritical: true,
    message: 'An unexpected error occurred during sync.',
    originalError: error,
  }
}

/**
 * Extracts HTTP status code from fetch response error.
 *
 * @param error - Error from fetch operation
 * @returns HTTP status code or undefined
 */
export function extractStatusCode(error: Error): number | undefined {
  // Try to extract from error message
  const match = error.message.match(/\b(\d{3})\b/)
  if (match) {
    return parseInt(match[1] ?? '', 10)
  }

  // Check if error has a response property (some fetch libraries attach this)
  const errorWithResponse = error as Error & { response?: { status?: number } }
  return errorWithResponse.response?.status
}

/**
 * Determines if an error should block the application.
 *
 * @param error - The error to check
 * @returns True if the error is critical and should block the app
 */
export function shouldBlockOnError(error: Error): boolean {
  return categorizeSyncError(error).isCritical
}

/**
 * Gets a user-friendly error message for display.
 *
 * @param error - The error to get a message for
 * @returns User-friendly error message
 */
export function getUserFriendlyErrorMessage(error: Error): string {
  return categorizeSyncError(error).message
}
